import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Plus, Trash2, Calendar, TrendingUp, Loader2, ChevronDown, ChevronUp, Pencil, Check, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PaperTradingPeriod {
  id: string;
  period_name: string;
  start_date: string;
  end_date: string;
  starting_capital: number;
  total_return_pct: number | null;
  sharpe_ratio: number | null;
  sortino_ratio: number | null;
  created_at: string;
}

interface PaperTradingTrade {
  id: string;
  period_id: string;
  entry_date: string;
  exit_date: string | null;
  stock_symbol: string;
  option_type: string;
  strike_price: number;
  expiration_date: string;
  result_pct: number | null;
  result_dollars: number | null;
  notes: string | null;
}

// Helper function to format date string (YYYY-MM-DD) for display without timezone shift
const formatDateForDisplay = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString();
};

// Helper function to get business days (Mon-Fri) within a date range
const getBusinessDays = (startDate: string, endDate: string): string[] => {
  const businessDays: string[] = [];
  
  // Parse dates without timezone issues
  const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
  
  const start = new Date(startYear, startMonth - 1, startDay);
  const end = new Date(endYear, endMonth - 1, endDay);
  
  const current = new Date(start);
  while (current <= end) {
    const dayOfWeek = current.getDay();
    // 0 = Sunday, 6 = Saturday
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      // Format as YYYY-MM-DD without timezone issues
      const year = current.getFullYear();
      const month = String(current.getMonth() + 1).padStart(2, '0');
      const day = String(current.getDate()).padStart(2, '0');
      businessDays.push(`${year}-${month}-${day}`);
    }
    current.setDate(current.getDate() + 1);
  }
  
  return businessDays;
};

const PaperTradingManager = () => {
  const { toast } = useToast();
  const [periods, setPeriods] = useState<PaperTradingPeriod[]>([]);
  const [trades, setTrades] = useState<Record<string, PaperTradingTrade[]>>({});
  const [expandedPeriod, setExpandedPeriod] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states for new period
  const [newPeriodName, setNewPeriodName] = useState('');
  const [newPeriodStartDate, setNewPeriodStartDate] = useState('');
  const [newPeriodEndDate, setNewPeriodEndDate] = useState('');
  const [newPeriodCapital, setNewPeriodCapital] = useState('10000');
  const [newPeriodSharpe, setNewPeriodSharpe] = useState('');
  const [newPeriodSortino, setNewPeriodSortino] = useState('');

  // Form states for new trade
  const [addingTradeForPeriod, setAddingTradeForPeriod] = useState<string | null>(null);
  const [newTradeSymbol, setNewTradeSymbol] = useState('');
  const [newTradeEntryDate, setNewTradeEntryDate] = useState('');
  const [newTradeExitDate, setNewTradeExitDate] = useState('');
  const [newTradeOptionType, setNewTradeOptionType] = useState<'call' | 'put'>('call');
  const [newTradeStrike, setNewTradeStrike] = useState('');
  const [newTradeExpiration, setNewTradeExpiration] = useState('');
  const [newTradeResultPct, setNewTradeResultPct] = useState('');
  const [newTradeResultDollars, setNewTradeResultDollars] = useState('');

  // Edit trade states
  const [editingTradeId, setEditingTradeId] = useState<string | null>(null);
  const [editTradeSymbol, setEditTradeSymbol] = useState('');
  const [editTradeEntryDate, setEditTradeEntryDate] = useState('');
  const [editTradeExitDate, setEditTradeExitDate] = useState('');
  const [editTradeOptionType, setEditTradeOptionType] = useState<'call' | 'put'>('call');
  const [editTradeStrike, setEditTradeStrike] = useState('');
  const [editTradeExpiration, setEditTradeExpiration] = useState('');
  const [editTradeResultPct, setEditTradeResultPct] = useState('');
  const [editTradeResultDollars, setEditTradeResultDollars] = useState('');

  // Delete confirmation
  const [periodToDelete, setPeriodToDelete] = useState<PaperTradingPeriod | null>(null);
  const [tradeToDelete, setTradeToDelete] = useState<PaperTradingTrade | null>(null);

  // Edit period name state
  const [editingPeriodId, setEditingPeriodId] = useState<string | null>(null);
  const [editPeriodName, setEditPeriodName] = useState('');

  const [hasRecalculated, setHasRecalculated] = useState(false);

  useEffect(() => {
    fetchPeriods();
  }, []);

  // Recalculate all period returns once on initial load
  useEffect(() => {
    if (periods.length > 0 && !hasRecalculated) {
      recalculateAllPeriodReturns();
      setHasRecalculated(true);
    }
  }, [periods.length, hasRecalculated]);

  const fetchPeriods = async () => {
    try {
      const { data, error } = await supabase
        .from('paper_trading_periods')
        .select('*')
        .order('start_date', { ascending: false });

      if (error) throw error;
      setPeriods(data || []);
    } catch (error) {
      console.error('Error fetching periods:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Recalculate all periods without triggering refetch loop
  const recalculateAllPeriodReturns = async () => {
    for (const period of periods) {
      await recalculatePeriodReturnSilent(period.id, period.starting_capital);
    }
    // Refetch once after all recalculations
    fetchPeriods();
  };

  // Silent version that doesn't refetch (used for bulk operations)
  const recalculatePeriodReturnSilent = async (periodId: string, startingCapital: number) => {
    try {
      const { data: tradesData } = await supabase
        .from('paper_trading_trades')
        .select('result_dollars')
        .eq('period_id', periodId);

      let totalPnLDollars = 0;
      if (tradesData && tradesData.length > 0) {
        tradesData.forEach(trade => {
          if (trade.result_dollars !== null) {
            totalPnLDollars += trade.result_dollars;
          }
        });
      }

      // Calculate return percentage: (Total P/L $) / Starting Capital * 100
      const returnPct = startingCapital > 0 ? (totalPnLDollars / startingCapital) * 100 : 0;

      await supabase
        .from('paper_trading_periods')
        .update({ total_return_pct: parseFloat(returnPct.toFixed(2)) })
        .eq('id', periodId);
    } catch (error) {
      console.error('Error recalculating period return:', error);
    }
  };

  const fetchTradesForPeriod = async (periodId: string) => {
    try {
      const { data, error } = await supabase
        .from('paper_trading_trades')
        .select('*')
        .eq('period_id', periodId)
        .order('entry_date', { ascending: true });

      if (error) throw error;
      setTrades(prev => ({ ...prev, [periodId]: data || [] }));
    } catch (error) {
      console.error('Error fetching trades:', error);
    }
  };

  const handleAddPeriod = async () => {
    if (!newPeriodName || !newPeriodStartDate || !newPeriodEndDate) {
      toast({
        title: "Missing Fields",
        description: "Please fill in period name and dates.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('paper_trading_periods')
        .insert({
          period_name: newPeriodName,
          start_date: newPeriodStartDate,
          end_date: newPeriodEndDate,
          starting_capital: parseFloat(newPeriodCapital) || 10000,
          sharpe_ratio: newPeriodSharpe ? parseFloat(newPeriodSharpe) : null,
          sortino_ratio: newPeriodSortino ? parseFloat(newPeriodSortino) : null,
          created_by: userData.user?.id,
        });

      if (error) throw error;

      toast({
        title: "Period Created",
        description: `Created paper trading period: ${newPeriodName}`,
      });

      // Reset form
      setNewPeriodName('');
      setNewPeriodStartDate('');
      setNewPeriodEndDate('');
      setNewPeriodCapital('10000');
      setNewPeriodSharpe('');
      setNewPeriodSortino('');
      
      fetchPeriods();
    } catch (error: any) {
      console.error('Error creating period:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create period.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get available business days for a period
  const getAvailableBusinessDaysForPeriod = (periodId: string): string[] => {
    const period = periods.find(p => p.id === periodId);
    if (!period) return [];
    
    const allBusinessDays = getBusinessDays(period.start_date, period.end_date);
    const usedDates = (trades[periodId] || []).map(t => t.entry_date);
    
    return allBusinessDays.filter(d => !usedDates.includes(d));
  };

  // Auto-select next available business day when opening add trade form
  const handleStartAddTrade = (periodId: string) => {
    setAddingTradeForPeriod(periodId);
    const availableDays = getAvailableBusinessDaysForPeriod(periodId);
    if (availableDays.length > 0) {
      setNewTradeEntryDate(availableDays[0]);
    }
  };

  const handleAddTrade = async (periodId: string) => {
    if (!newTradeSymbol || !newTradeEntryDate || !newTradeStrike || !newTradeExpiration) {
      toast({
        title: "Missing Fields",
        description: "Please fill in all required trade details.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('paper_trading_trades')
        .insert({
          period_id: periodId,
          stock_symbol: newTradeSymbol.toUpperCase(),
          entry_date: newTradeEntryDate,
          exit_date: newTradeExitDate || null,
          option_type: newTradeOptionType,
          strike_price: parseFloat(newTradeStrike),
          expiration_date: newTradeExpiration,
          result_pct: newTradeResultPct ? parseFloat(newTradeResultPct) : null,
          result_dollars: newTradeResultDollars ? parseFloat(newTradeResultDollars) : null,
        });

      if (error) throw error;

      toast({
        title: "Trade Added",
        description: `Added ${newTradeSymbol.toUpperCase()} ${newTradeOptionType.toUpperCase()} trade`,
      });

      // Reset form
      setNewTradeSymbol('');
      setNewTradeEntryDate('');
      setNewTradeExitDate('');
      setNewTradeOptionType('call');
      setNewTradeStrike('');
      setNewTradeExpiration('');
      setNewTradeResultPct('');
      setNewTradeResultDollars('');
      setAddingTradeForPeriod(null);

      // Refresh trades and recalculate period return
      fetchTradesForPeriod(periodId);
      recalculatePeriodReturn(periodId);
    } catch (error: any) {
      console.error('Error adding trade:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add trade.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Start editing a trade
  const handleStartEditTrade = (trade: PaperTradingTrade) => {
    setEditingTradeId(trade.id);
    setEditTradeSymbol(trade.stock_symbol);
    setEditTradeEntryDate(trade.entry_date);
    setEditTradeExitDate(trade.exit_date || '');
    setEditTradeOptionType(trade.option_type as 'call' | 'put');
    setEditTradeStrike(trade.strike_price.toString());
    setEditTradeExpiration(trade.expiration_date);
    setEditTradeResultPct(trade.result_pct?.toString() || '');
    setEditTradeResultDollars(trade.result_dollars?.toString() || '');
  };

  // Cancel editing
  const handleCancelEditTrade = () => {
    setEditingTradeId(null);
    setEditTradeSymbol('');
    setEditTradeEntryDate('');
    setEditTradeExitDate('');
    setEditTradeOptionType('call');
    setEditTradeStrike('');
    setEditTradeExpiration('');
    setEditTradeResultPct('');
    setEditTradeResultDollars('');
  };

  // Save edited trade
  const handleSaveEditTrade = async (trade: PaperTradingTrade) => {
    if (!editTradeSymbol || !editTradeEntryDate || !editTradeStrike || !editTradeExpiration) {
      toast({
        title: "Missing Fields",
        description: "Please fill in all required trade details.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('paper_trading_trades')
        .update({
          stock_symbol: editTradeSymbol.toUpperCase(),
          entry_date: editTradeEntryDate,
          exit_date: editTradeExitDate || null,
          option_type: editTradeOptionType,
          strike_price: parseFloat(editTradeStrike),
          expiration_date: editTradeExpiration,
          result_pct: editTradeResultPct ? parseFloat(editTradeResultPct) : null,
          result_dollars: editTradeResultDollars ? parseFloat(editTradeResultDollars) : null,
        })
        .eq('id', trade.id);

      if (error) throw error;

      toast({
        title: "Trade Updated",
        description: `Updated ${editTradeSymbol.toUpperCase()} trade`,
      });

      handleCancelEditTrade();
      fetchTradesForPeriod(trade.period_id);
      recalculatePeriodReturn(trade.period_id);
    } catch (error: any) {
      console.error('Error updating trade:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update trade.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const recalculatePeriodReturn = async (periodId: string) => {
    try {
      // Fetch period to get starting capital
      const { data: periodData } = await supabase
        .from('paper_trading_periods')
        .select('starting_capital')
        .eq('id', periodId)
        .maybeSingle();

      if (!periodData) return;

      const { data: tradesData } = await supabase
        .from('paper_trading_trades')
        .select('result_dollars')
        .eq('period_id', periodId);

      if (tradesData && tradesData.length > 0) {
        // Calculate total P/L in dollars
        let totalPnLDollars = 0;
        tradesData.forEach(trade => {
          if (trade.result_dollars !== null) {
            totalPnLDollars += trade.result_dollars;
          }
        });

        // Calculate return percentage: (Total P/L $) / Starting Capital * 100
        const returnPct = (totalPnLDollars / periodData.starting_capital) * 100;

        await supabase
          .from('paper_trading_periods')
          .update({ total_return_pct: parseFloat(returnPct.toFixed(2)) })
          .eq('id', periodId);

        fetchPeriods();
      } else {
        // No trades, set return to 0
        await supabase
          .from('paper_trading_periods')
          .update({ total_return_pct: 0 })
          .eq('id', periodId);

        fetchPeriods();
      }
    } catch (error) {
      console.error('Error recalculating period return:', error);
    }
  };

  const handleDeletePeriod = async () => {
    if (!periodToDelete) return;
    
    try {
      const { error } = await supabase
        .from('paper_trading_periods')
        .delete()
        .eq('id', periodToDelete.id);

      if (error) throw error;

      toast({
        title: "Period Deleted",
        description: `Deleted period: ${periodToDelete.period_name}`,
      });

      setPeriodToDelete(null);
      fetchPeriods();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete period.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTrade = async () => {
    if (!tradeToDelete) return;
    
    try {
      const periodId = tradeToDelete.period_id;
      
      const { error } = await supabase
        .from('paper_trading_trades')
        .delete()
        .eq('id', tradeToDelete.id);

      if (error) throw error;

      toast({
        title: "Trade Deleted",
        description: `Deleted ${tradeToDelete.stock_symbol} trade`,
      });

      setTradeToDelete(null);
      fetchTradesForPeriod(periodId);
      recalculatePeriodReturn(periodId);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete trade.",
        variant: "destructive",
      });
    }
  };

  // Start editing a period name
  const handleStartEditPeriodName = (period: PaperTradingPeriod) => {
    setEditingPeriodId(period.id);
    setEditPeriodName(period.period_name);
  };

  // Cancel editing period name
  const handleCancelEditPeriodName = () => {
    setEditingPeriodId(null);
    setEditPeriodName('');
  };

  // Save edited period name
  const handleSaveEditPeriodName = async (periodId: string) => {
    if (!editPeriodName.trim()) {
      toast({
        title: "Invalid Name",
        description: "Period name cannot be empty.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('paper_trading_periods')
        .update({ period_name: editPeriodName.trim() })
        .eq('id', periodId);

      if (error) throw error;

      toast({
        title: "Period Updated",
        description: `Period name updated to: ${editPeriodName.trim()}`,
      });

      handleCancelEditPeriodName();
      fetchPeriods();
    } catch (error: any) {
      console.error('Error updating period name:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update period name.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePeriodExpansion = (periodId: string) => {
    if (expandedPeriod === periodId) {
      setExpandedPeriod(null);
    } else {
      setExpandedPeriod(periodId);
      if (!trades[periodId]) {
        fetchTradesForPeriod(periodId);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-delphi-red" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add New Period Form */}
      <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20">
        <CardHeader>
          <CardTitle className="text-lg text-delphi-light flex items-center">
            <Calendar size={18} className="mr-2 text-delphi-red" />
            Add Paper Trading Period
          </CardTitle>
          <CardDescription>Record historical paper trading trial periods for investor demos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="text-delphi-light/70 text-sm mb-2 block">Period Name</Label>
              <Input
                placeholder="e.g. Q1 2024 Trial"
                value={newPeriodName}
                onChange={(e) => setNewPeriodName(e.target.value)}
                className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
              />
            </div>
            <div>
              <Label className="text-delphi-light/70 text-sm mb-2 block">Start Date</Label>
              <Input
                type="date"
                value={newPeriodStartDate}
                onChange={(e) => setNewPeriodStartDate(e.target.value)}
                className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
              />
            </div>
            <div>
              <Label className="text-delphi-light/70 text-sm mb-2 block">End Date</Label>
              <Input
                type="date"
                value={newPeriodEndDate}
                onChange={(e) => setNewPeriodEndDate(e.target.value)}
                className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="text-delphi-light/70 text-sm mb-2 block">Starting Capital ($)</Label>
              <Input
                type="number"
                placeholder="10000"
                value={newPeriodCapital}
                onChange={(e) => setNewPeriodCapital(e.target.value)}
                className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
              />
            </div>
            <div>
              <Label className="text-delphi-light/70 text-sm mb-2 block">Sharpe Ratio (optional)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="e.g. 1.21"
                value={newPeriodSharpe}
                onChange={(e) => setNewPeriodSharpe(e.target.value)}
                className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
              />
            </div>
            <div>
              <Label className="text-delphi-light/70 text-sm mb-2 block">Sortino Ratio (optional)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="e.g. 3.18"
                value={newPeriodSortino}
                onChange={(e) => setNewPeriodSortino(e.target.value)}
                className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
              />
            </div>
          </div>
          <Button
            onClick={handleAddPeriod}
            disabled={isSubmitting}
            className="bg-delphi-red hover:bg-delphi-red/80"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
            Create Period
          </Button>
        </CardContent>
      </Card>

      {/* Existing Periods */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-delphi-light">Paper Trading Periods</h3>
        
        {periods.length === 0 ? (
          <Card className="bg-delphi-dark/50 border border-delphi-gray/20">
            <CardContent className="py-8 text-center text-delphi-light/60">
              No paper trading periods recorded yet. Create one above.
            </CardContent>
          </Card>
        ) : (
          periods.map((period) => (
            <Card key={period.id} className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-gray/30">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div 
                    className="flex items-center cursor-pointer flex-1"
                    onClick={() => togglePeriodExpansion(period.id)}
                  >
                    <div className="mr-3">
                      {expandedPeriod === period.id ? (
                        <ChevronUp size={20} className="text-delphi-light/60" />
                      ) : (
                        <ChevronDown size={20} className="text-delphi-light/60" />
                      )}
                    </div>
                    <div>
                      {editingPeriodId === period.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editPeriodName}
                            onChange={(e) => setEditPeriodName(e.target.value)}
                            className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-8 text-sm w-48"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.stopPropagation();
                                handleSaveEditPeriodName(period.id);
                              } else if (e.key === 'Escape') {
                                e.stopPropagation();
                                handleCancelEditPeriodName();
                              }
                            }}
                            autoFocus
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveEditPeriodName(period.id);
                            }}
                            disabled={isSubmitting}
                          >
                            <Check size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-delphi-light/60 hover:text-delphi-light hover:bg-delphi-gray/20"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelEditPeriodName();
                            }}
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-base text-delphi-light">{period.period_name}</CardTitle>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-delphi-light/40 hover:text-delphi-light hover:bg-delphi-gray/20"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEditPeriodName(period);
                            }}
                          >
                            <Pencil size={12} />
                          </Button>
                        </div>
                      )}
                      <CardDescription>
                        {formatDateForDisplay(period.start_date)} - {formatDateForDisplay(period.end_date)}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className={`text-lg font-bold ${(period.total_return_pct || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {period.total_return_pct !== null ? `${period.total_return_pct >= 0 ? '+' : ''}${period.total_return_pct.toFixed(2)}%` : 'N/A'}
                      </p>
                      <p className="text-xs text-delphi-light/50">Total Return</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPeriodToDelete(period);
                      }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              {expandedPeriod === period.id && (
                <CardContent className="pt-0">
                  <div className="border-t border-delphi-gray/20 pt-4">
                    {/* Period Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                      <div className="bg-delphi-gray/10 p-3 rounded">
                        <p className="text-delphi-light/60">Starting Capital</p>
                        <p className="text-delphi-light font-medium">${period.starting_capital.toLocaleString()}</p>
                      </div>
                      {period.sharpe_ratio && (
                        <div className="bg-delphi-gray/10 p-3 rounded">
                          <p className="text-delphi-light/60">Sharpe Ratio</p>
                          <p className="text-delphi-light font-medium">{period.sharpe_ratio.toFixed(2)}</p>
                        </div>
                      )}
                      {period.sortino_ratio && (
                        <div className="bg-delphi-gray/10 p-3 rounded">
                          <p className="text-delphi-light/60">Sortino Ratio</p>
                          <p className="text-delphi-light font-medium">{period.sortino_ratio.toFixed(2)}</p>
                        </div>
                      )}
                    </div>

                    {/* Trades List */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-delphi-light mb-2">Trades</h4>
                      {trades[period.id]?.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-delphi-gray/20">
                                <th className="text-left py-2 text-delphi-light/60 font-medium">Date</th>
                                <th className="text-left py-2 text-delphi-light/60 font-medium">Ticker</th>
                                <th className="text-left py-2 text-delphi-light/60 font-medium">Type</th>
                                <th className="text-left py-2 text-delphi-light/60 font-medium">Strike</th>
                                <th className="text-left py-2 text-delphi-light/60 font-medium">Exp</th>
                                <th className="text-right py-2 text-delphi-light/60 font-medium">Return %</th>
                                <th className="text-right py-2 text-delphi-light/60 font-medium">Return $</th>
                                <th className="text-right py-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {trades[period.id].map((trade) => (
                                editingTradeId === trade.id ? (
                                  // Editing row
                                  <tr key={trade.id} className="border-b border-delphi-gray/10 bg-delphi-gray/5">
                                    <td className="py-2">
                                      <Input
                                        type="date"
                                        value={editTradeEntryDate}
                                        onChange={(e) => setEditTradeEntryDate(e.target.value)}
                                        className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-7 text-xs w-28"
                                      />
                                    </td>
                                    <td className="py-2">
                                      <Input
                                        value={editTradeSymbol}
                                        onChange={(e) => setEditTradeSymbol(e.target.value.toUpperCase())}
                                        className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-7 text-xs w-16"
                                      />
                                    </td>
                                    <td className="py-2">
                                      <div className="flex gap-1">
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant={editTradeOptionType === 'call' ? 'default' : 'outline'}
                                          className={editTradeOptionType === 'call' 
                                            ? 'bg-green-600 hover:bg-green-700 h-7 text-xs px-2' 
                                            : 'border-delphi-gray/40 text-delphi-light/70 h-7 text-xs px-2'}
                                          onClick={() => setEditTradeOptionType('call')}
                                        >
                                          C
                                        </Button>
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant={editTradeOptionType === 'put' ? 'default' : 'outline'}
                                          className={editTradeOptionType === 'put' 
                                            ? 'bg-red-600 hover:bg-red-700 h-7 text-xs px-2' 
                                            : 'border-delphi-gray/40 text-delphi-light/70 h-7 text-xs px-2'}
                                          onClick={() => setEditTradeOptionType('put')}
                                        >
                                          P
                                        </Button>
                                      </div>
                                    </td>
                                    <td className="py-2">
                                      <Input
                                        type="number"
                                        value={editTradeStrike}
                                        onChange={(e) => setEditTradeStrike(e.target.value)}
                                        className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-7 text-xs w-20"
                                      />
                                    </td>
                                    <td className="py-2">
                                      <Input
                                        type="date"
                                        value={editTradeExpiration}
                                        onChange={(e) => setEditTradeExpiration(e.target.value)}
                                        className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-7 text-xs w-28"
                                      />
                                    </td>
                                    <td className="py-2">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editTradeResultPct}
                                        onChange={(e) => setEditTradeResultPct(e.target.value)}
                                        className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-7 text-xs w-20"
                                      />
                                    </td>
                                    <td className="py-2">
                                      <Input
                                        type="number"
                                        value={editTradeResultDollars}
                                        onChange={(e) => setEditTradeResultDollars(e.target.value)}
                                        className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-7 text-xs w-20"
                                      />
                                    </td>
                                    <td className="py-2 text-right">
                                      <div className="flex gap-1 justify-end">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6 text-green-400 hover:text-green-300"
                                          onClick={() => handleSaveEditTrade(trade)}
                                          disabled={isSubmitting}
                                        >
                                          {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6 text-delphi-light/60 hover:text-delphi-light"
                                          onClick={handleCancelEditTrade}
                                        >
                                          <X size={14} />
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                ) : (
                                  // Display row
                                  <tr key={trade.id} className="border-b border-delphi-gray/10">
                                    <td className="py-2 text-delphi-light/80">
                                      {formatDateForDisplay(trade.entry_date)}
                                    </td>
                                    <td className="py-2 text-delphi-light font-medium">{trade.stock_symbol}</td>
                                    <td className={`py-2 ${trade.option_type === 'call' ? 'text-green-400' : 'text-red-400'}`}>
                                      {trade.option_type.toUpperCase()}
                                    </td>
                                    <td className="py-2 text-delphi-light/80">${trade.strike_price}</td>
                                    <td className="py-2 text-delphi-light/60">
                                      {formatDateForDisplay(trade.expiration_date)}
                                    </td>
                                    <td className={`py-2 text-right ${(trade.result_pct || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      {trade.result_pct !== null ? `${trade.result_pct >= 0 ? '+' : ''}${trade.result_pct.toFixed(2)}%` : '-'}
                                    </td>
                                    <td className={`py-2 text-right ${(trade.result_dollars || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      {trade.result_dollars !== null ? `$${trade.result_dollars.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="py-2 text-right">
                                      <div className="flex gap-1 justify-end">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6 text-delphi-light/60 hover:text-delphi-light"
                                          onClick={() => handleStartEditTrade(trade)}
                                        >
                                          <Pencil size={14} />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6 text-red-400 hover:text-red-300"
                                          onClick={() => setTradeToDelete(trade)}
                                        >
                                          <Trash2 size={14} />
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                )
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-delphi-light/50 text-sm">No trades recorded for this period.</p>
                      )}
                    </div>

                    {/* Add Trade Form */}
                    {addingTradeForPeriod === period.id ? (
                      <div className="bg-delphi-gray/10 p-4 rounded-lg">
                        <h5 className="text-sm font-medium text-delphi-light mb-3">Add Trade</h5>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          <div>
                            <Label className="text-xs text-delphi-light/60">Entry Date</Label>
                            <select
                              value={newTradeEntryDate}
                              onChange={(e) => setNewTradeEntryDate(e.target.value)}
                              className="w-full bg-delphi-dark text-delphi-light border border-delphi-gray/40 rounded-md h-8 text-sm px-2"
                            >
                              <option value="">Select date...</option>
                              {getAvailableBusinessDaysForPeriod(period.id).map(date => (
                                <option key={date} value={date}>
                                  {new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs text-delphi-light/60">Ticker</Label>
                            <Input
                              placeholder="SPY"
                              value={newTradeSymbol}
                              onChange={(e) => setNewTradeSymbol(e.target.value.toUpperCase())}
                              className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-delphi-light/60">Type</Label>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                size="sm"
                                variant={newTradeOptionType === 'call' ? 'default' : 'outline'}
                                className={newTradeOptionType === 'call' 
                                  ? 'flex-1 bg-green-600 hover:bg-green-700 h-8 text-xs' 
                                  : 'flex-1 border-delphi-gray/40 text-delphi-light/70 h-8 text-xs'}
                                onClick={() => setNewTradeOptionType('call')}
                              >
                                CALL
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant={newTradeOptionType === 'put' ? 'default' : 'outline'}
                                className={newTradeOptionType === 'put' 
                                  ? 'flex-1 bg-red-600 hover:bg-red-700 h-8 text-xs' 
                                  : 'flex-1 border-delphi-gray/40 text-delphi-light/70 h-8 text-xs'}
                                onClick={() => setNewTradeOptionType('put')}
                              >
                                PUT
                              </Button>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-delphi-light/60">Strike</Label>
                            <Input
                              type="number"
                              placeholder="$500"
                              value={newTradeStrike}
                              onChange={(e) => setNewTradeStrike(e.target.value)}
                              className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-8 text-sm"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          <div>
                            <Label className="text-xs text-delphi-light/60">Exp Date</Label>
                            <Input
                              type="date"
                              value={newTradeExpiration}
                              onChange={(e) => setNewTradeExpiration(e.target.value)}
                              className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-delphi-light/60">Exit Date (opt)</Label>
                            <Input
                              type="date"
                              value={newTradeExitDate}
                              onChange={(e) => setNewTradeExitDate(e.target.value)}
                              className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-delphi-light/60">Return %</Label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="+25.5"
                              value={newTradeResultPct}
                              onChange={(e) => setNewTradeResultPct(e.target.value)}
                              className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-delphi-light/60">Return $</Label>
                            <Input
                              type="number"
                              placeholder="$500"
                              value={newTradeResultDollars}
                              onChange={(e) => setNewTradeResultDollars(e.target.value)}
                              className="bg-delphi-dark text-delphi-light border-delphi-gray/40 h-8 text-sm"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAddTrade(period.id)}
                            disabled={isSubmitting}
                            className="bg-delphi-red hover:bg-delphi-red/80"
                          >
                            {isSubmitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                            Save Trade
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setAddingTradeForPeriod(null)}
                            className="border-delphi-gray/40 text-delphi-light/70"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStartAddTrade(period.id)}
                        className="border-delphi-gray/40 text-delphi-light/70"
                      >
                        <Plus size={14} className="mr-1" />
                        Add Trade
                      </Button>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Delete Period Confirmation */}
      <AlertDialog open={!!periodToDelete} onOpenChange={() => setPeriodToDelete(null)}>
        <AlertDialogContent className="bg-delphi-dark border-delphi-gray/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-delphi-light">Delete Paper Trading Period?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{periodToDelete?.period_name}" and all associated trades. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-delphi-gray/20 text-delphi-light border-delphi-gray/40">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeletePeriod}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Period
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Trade Confirmation */}
      <AlertDialog open={!!tradeToDelete} onOpenChange={() => setTradeToDelete(null)}>
        <AlertDialogContent className="bg-delphi-dark border-delphi-gray/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-delphi-light">Delete Trade?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the {tradeToDelete?.stock_symbol} trade. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-delphi-gray/20 text-delphi-light border-delphi-gray/40">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteTrade}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Trade
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PaperTradingManager;
